import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

type RequestItem = {
  id: string;
  customer_id: string;
  customer_name?: string;
  requested: Record<string, any>;
  snapshot_current: Record<string, any>;
  created_at: string;
  status?: 'pending' | 'approved' | 'rejected';
  decided_at?: string | null;
  decided_by?: string | null;
};

export default function PortalAdminPage() {
  const { API_BASE_URL, isCookieMode, token } = useAuth();
  const [items, setItems] = useState<RequestItem[]>([]);
  const [history, setHistory] = useState<RequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchRequests() {
    setLoading(true);
    setError('');
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE_URL}/portal-admin/customer-update-requests?status=pending`, {
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }

  async function fetchHistory() {
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE_URL}/portal-admin/customer-update-requests?status=all`, {
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      // Exclude current pending list duplicates if any
      setHistory(Array.isArray(data) ? data.filter((d: RequestItem) => d.status !== 'pending') : []);
    } catch (_e) {
      // Non-fatal if history fails
      setHistory([]);
    }
  }

  useEffect(() => { fetchRequests(); fetchHistory(); }, []);

  async function act(id: string, action: 'approve' | 'reject') {
    try {
      const headers: any = { 'Content-Type': 'application/json' };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE_URL}/portal-admin/customer-update-requests/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
			setItems(prev => prev.filter(i => i.id !== id));
			// Ensure history reflects the latest decision immediately
			await fetchHistory();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Failed to perform action');
    }
  }

  function renderDiff(requested: Record<string, any>, snapshot: Record<string, any>) {
    const keys = Array.from(new Set([...Object.keys(requested || {}), ...Object.keys(snapshot || {})]));
    return (
      <table className="min-w-full text-sm">
        <thead>
          <tr>
            <th className="px-3 py-1 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Field</th>
            <th className="px-3 py-1 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Current</th>
            <th className="px-3 py-1 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Requested</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {keys.map(k => {
            const before = snapshot?.[k] ?? '';
            const after = requested?.[k] ?? '';
            const changed = String(before) !== String(after);
            return (
              <tr key={k} className="odd:bg-white even:bg-gray-50/40">
                <td className="px-3 py-1 font-medium text-gray-800">{k}</td>
                <td className="px-3 py-1 text-gray-700">{String(before || '')}</td>
                <td className={`px-3 py-1 ${changed ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{String(after || '')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function ItemHeader({ item, actions }: { item: RequestItem; actions?: React.ReactNode }) {
    const created = new Date(item.created_at);
    const createdStr = isNaN(created.getTime()) ? item.created_at : created.toLocaleString();
    return (
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-700">Request ID: <span className="font-semibold text-gray-900">{item.id}</span></div>
          <div className="text-sm text-gray-700">Requested by: <span className="font-semibold text-gray-900">{item.customer_name || item.customer_id}</span></div>
          <div className="text-sm text-gray-700">Requested at: <span className="font-semibold text-gray-900">{createdStr}</span></div>
          {item.status && item.status !== 'pending' && (
            <div className="text-sm text-gray-700">Status: <span className={`font-semibold ${item.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>{item.status}</span>{item.decided_at ? ` on ${new Date(item.decided_at).toLocaleString()}` : ''}</div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2">{actions}</div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="rounded-2xl border border-gray-200 bg-white elev-1">
        <div className="p-5 border-b border-gray-100">
          <h1 className="text-2xl font-extrabold text-gray-900">Portal Admin — Customer Update Requests</h1>
        </div>
        {loading ? (
          <div className="p-6">Loading…</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-gray-700">No pending requests</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map(item => (
              <li key={item.id} className="p-5">
                <ItemHeader
                  item={item}
                  actions={
                    <>
                      <button onClick={() => act(item.id, 'approve')} className="rounded-xl bg-green-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-green-700 focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2">Approve</button>
                      <button onClick={() => act(item.id, 'reject')} className="rounded-xl border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">Reject</button>
                    </>
                  }
                />
                <div className="mt-4 overflow-auto">
                  {renderDiff(item.requested || {}, item.snapshot_current || {})}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* History section */}
      <div className="rounded-2xl border border-gray-200 bg-white elev-1 mt-6">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-xl font-extrabold text-gray-900">History — All Customer Update Requests</h2>
          <p className="text-sm text-gray-600">Approved and rejected requests are retained for audit history.</p>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-gray-700">No history yet</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {history.map(item => (
              <li key={item.id} className="p-5">
                <details>
                  <summary className="list-none cursor-pointer">
                    <ItemHeader item={item} />
                  </summary>
                  <div className="mt-4 overflow-auto">
                    {renderDiff(item.requested || {}, item.snapshot_current || {})}
                  </div>
                </details>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


